import { useSearchParams } from 'react-router-dom'
import { faCoins, faStars } from '@fortawesome/pro-regular-svg-icons'
import { isNumber } from 'lodash'

import { centsToCurrency, formatCurrency } from '@/utils/currencyHelpers'

import {
  QuarterlyTaxEstimateDetail,
  QuarterlyTaxEstimateDetailStatuses,
} from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'
import {
  isTaxCalculationOutput,
  UserTaxCalculation,
} from '@/features/admin/user-tax-calculations/userTaxCalculation.slice'
import {
  FILING_STATUSES,
  TAX_ENTITY_TYPES,
} from '@/features/taxes/taxConstants'

import {
  HighEstimatesAlert,
  getIsTaxCalcIncomeHigh,
} from '../../../../HighEstimatesAlert'
import {
  DeductiblePotionSelfEmploymentTooltip,
  OtherW2IncomeEstimatedTooltip,
  OtherW2IncomeTooltip,
  PrivatePracticeNetIncomeTooltip,
  QBIDeductionTooltip,
  Scorp1099IncomeTooltip,
  ScorpEstimatedPrivatePracticeIncomeTooltip,
  ScorpPrivatePracticeIncomeTooltip,
  ScorpProjectedAnnualIncomeTooltip,
  Sprop1099IncomeTooltip,
  SpropProjectedAnnualIncomeTooltip,
  StandardDeductionTooltip,
  TotalTaxableIncomeTooltip,
} from '../../EstimateBreakdownV3Tooltips'

import { centsToDollarsOrDashes, centsToSubtractedAmount } from '../../utils'
import { useBreakdownTransparency } from '../../useBreakdownTransparency'

import { AccordionHeader } from '../shared/accordion-header'
import { BreakdownTooltip } from '../shared/breakdown-tooltip'
import { CalculationRow } from '../shared/calculation-row'
import { PencilLinkOrText } from '../shared/pencil-link-or-text'

import { TextColors, TypographyStyles } from '@/styles'
import {
  Accordion,
  Button,
  Card,
  Icon,
  Text,
  Link,
  Alert,
  Badge,
  CardColors,
  Separator,
  Popover,
} from '@/ui'

const isScorpCalc = (taxCalc: UserTaxCalculation | undefined) =>
  taxCalc?.taxEntityType === TAX_ENTITY_TYPES.form_1120_s

const doesCalcHaveSpouse = (taxCalc: UserTaxCalculation | undefined) =>
  taxCalc?.filingStatus &&
  [
    FILING_STATUSES.married_filing_jointly,
    FILING_STATUSES.qualifying_widow,
  ].includes(taxCalc.filingStatus)

const CalculatedStatement = () => (
  <Text color={TextColors.MUTED}>
    <i>Calculated from your profit and loss statement</i>
  </Text>
)

const DeductionStatement = () => (
  <Text color={TextColors.MUTED}>
    <i>
      Our quarterly tax estimator uses the &quot;standard deduction&quot;
      amount. Currently, we&apos;re not able to include pre-tax deductions like
      retirement contributions, investment returns or military tax-exemptions,
      but we will be able to in future quarters.{' '}
      <Link
        href="https://support.joinheard.com/hc/en-us/articles/4416208116247"
        newPage
      >
        Learn more
      </Link>
    </i>
  </Text>
)

interface IncomeSectionProps {
  taxCalc: UserTaxCalculation | undefined
  estimateDetails: QuarterlyTaxEstimateDetail | undefined
  isSample?: boolean
}

export const IncomeSection = ({
  estimateDetails,
  taxCalc,
  isSample,
}: IncomeSectionProps) => {
  const [searchParams] = useSearchParams()
  const taxCalculationOutput = taxCalc?.taxCalculationOutput
  const isScorp = isScorpCalc(taxCalc)
  const hasSpouse = doesCalcHaveSpouse(taxCalc)
  const incomeExpanded = searchParams.get('incomeExpanded') === 'true'
  const isIncomeHigh = getIsTaxCalcIncomeHigh(taxCalc)
  const transparencyEnabled = useBreakdownTransparency()

  // A $0 deduction is a real answer — an S-corp owner with no 1099 income gets
  // no QBI deduction — so it reads as $0.00 rather than as a missing value.
  const formatDeduction = (amountInCents: number | null | undefined) =>
    transparencyEnabled
      ? centsToSubtractedAmount(amountInCents)
      : centsToDollarsOrDashes(amountInCents, true)
  const quarter = estimateDetails?.taxQuarter
  const year = estimateDetails?.taxYear
  const isActiveEstimate =
    estimateDetails?.status === QuarterlyTaxEstimateDetailStatuses.active

  // This check is a little confusing - it's making sure that tax calc output is either undefined or it matches the expected shape
  if (
    !isTaxCalculationOutput(taxCalculationOutput) &&
    taxCalculationOutput !== undefined
  ) {
    return null
  }

  return (
    <>
      <div className="flex items-center gap-4">
        <Text variant={TypographyStyles.H2} className="flex items-center">
          <Icon icon={faCoins} className="mr-2 inline-block" />
          Income
        </Text>
        {isSample && <Badge color="orange">Sample Data</Badge>}
      </div>

      <div className="max-w-3xl">
        <Text color={TextColors.MUTED}>
          {isScorp ? (
            <>
              Based on the numbers you updated for{' '}
              {isActiveEstimate ? (
                <Link disabled={isSample} href="/taxes/quarterly/recalculate">
                  your Q{quarter} tax information
                </Link>
              ) : (
                `your Q${quarter} tax information`
              )}
              , this is how much we&apos;re estimating you&apos;ll earn for the
              year (before tax deductions).
            </>
          ) : (
            <>
              Based on your private practice&apos;s net income and any
              additional income sources from when you updated{' '}
              {isActiveEstimate ? (
                <Link disabled={isSample} href="/taxes/quarterly/recalculate">
                  your Q{quarter} tax information
                </Link>
              ) : (
                `your Q${quarter} tax information`
              )}
              , this is how much we&apos;re estimating you&apos;ll earn this
              year (before tax deductions).
            </>
          )}
        </Text>
      </div>

      {isActiveEstimate && (
        <HighEstimatesAlert
          title="The income you entered seems high"
          taxCalc={taxCalc}
        />
      )}

      {/*  Only one alert should display at a time*/}
      {isActiveEstimate && incomeExpanded && !isIncomeHigh && (
        <Alert
          title="Do these numbers look off?"
          variant="warning"
          customIconName={faStars}
        >
          Check that your entries are year-to-date (from January to the present)
          instead of annual (the entire year).{' '}
          <Link
            disabled={isSample}
            href="/taxes/quarterly/recalculate"
            variant="secondary"
          >
            Make Adjustments
          </Link>
        </Alert>
      )}

      <Accordion
        defaultValue={incomeExpanded || isIncomeHigh ? 'income' : undefined}
        variant="text"
        type="single"
        className="w-full"
        collapsible
      >
        <Accordion.Item value="income">
          <Accordion.Trigger className="w-full">
            <AccordionHeader
              left={
                <BreakdownTooltip
                  trigger="Projected Annual Income"
                  content={
                    isScorp ? (
                      <ScorpProjectedAnnualIncomeTooltip />
                    ) : (
                      <SpropProjectedAnnualIncomeTooltip />
                    )
                  }
                />
              }
              right={
                <Text variant={TypographyStyles.H3}>
                  {centsToDollarsOrDashes(
                    taxCalculationOutput?.data.annualGrossIncomeInCents
                  )}
                </Text>
              }
            />
          </Accordion.Trigger>
          <Accordion.Content>
            <Card color={CardColors.LEVEL_1}>
              <div className="flex flex-col gap-6">
                <CalculationRow
                  left={
                    <Text
                      variant={TypographyStyles.EYEBROW}
                      color={TextColors.MUTED}
                    >
                      income source
                    </Text>
                  }
                  center={
                    <Text
                      variant={TypographyStyles.EYEBROW}
                      className="text-right"
                      color={TextColors.MUTED}
                    >
                      your entries
                    </Text>
                  }
                  right={
                    <Text
                      variant={TypographyStyles.EYEBROW}
                      className="text-right"
                      color={TextColors.MUTED}
                    >
                      projected income
                    </Text>
                  }
                  inCard
                />

                {isScorp &&
                  taxCalc?.estimatedAnnualScorpPracticeW2IncomeInCents !==
                    null && (
                    <CalculationRow
                      left={
                        <BreakdownTooltip
                          trigger="W-2 private practice income (Estimated)"
                          content={
                            <ScorpEstimatedPrivatePracticeIncomeTooltip />
                          }
                        />
                      }
                      center={
                        <PencilLinkOrText
                          isActiveEstimate={isActiveEstimate}
                          hideLink={isSample}
                        >
                          {centsToDollarsOrDashes(
                            taxCalc?.estimatedAnnualScorpPracticeW2IncomeInCents
                          )}
                        </PencilLinkOrText>
                      }
                      right={
                        <Text variant={TypographyStyles.H3}>
                          {centsToDollarsOrDashes(
                            taxCalc?.annualizedScorpPracticeWIncomeInCents
                          )}
                        </Text>
                      }
                      inCard
                    />
                  )}

                {isScorp && taxCalc?.scorpPracticeWIncomeInCents !== null && (
                  <CalculationRow
                    left={
                      <BreakdownTooltip
                        trigger="W-2 private practice income (YTD)"
                        content={<ScorpPrivatePracticeIncomeTooltip />}
                      />
                    }
                    center={
                      <PencilLinkOrText
                        isActiveEstimate={isActiveEstimate}
                        hideLink={isSample}
                      >
                        {centsToDollarsOrDashes(
                          taxCalc?.scorpPracticeWIncomeInCents
                        )}
                      </PencilLinkOrText>
                    }
                    right={
                      <Text variant={TypographyStyles.H3}>
                        {centsToDollarsOrDashes(
                          taxCalc?.annualizedScorpPracticeWIncomeInCents
                        )}
                      </Text>
                    }
                    inCard
                  />
                )}

                <CalculationRow
                  left={
                    <>
                      <BreakdownTooltip
                        trigger="Private practice net income"
                        content={<PrivatePracticeNetIncomeTooltip />}
                      />
                      <div className="hidden md:block mt-1">
                        <CalculatedStatement />
                      </div>
                    </>
                  }
                  center={
                    <PencilLinkOrText
                      isActiveEstimate={isActiveEstimate}
                      hideLink
                    >
                      {isNumber(taxCalc?.periodIncomeInCents) &&
                      isNumber(taxCalc?.periodExpensesInCents)
                        ? formatCurrency(
                            centsToCurrency(
                              taxCalc.periodIncomeInCents
                            ).subtract(taxCalc.periodExpensesInCents)
                          )
                        : '--'}
                    </PencilLinkOrText>
                  }
                  right={
                    <Text variant={TypographyStyles.H3}>
                      {centsToDollarsOrDashes(
                        taxCalc?.annualizedNetIncomeInCents
                      )}
                    </Text>
                  }
                  inCard
                />

                <div className="md:hidden">
                  <CalculatedStatement />
                </div>

                {isNumber(taxCalc?.wIncomeInCents) && (
                  <CalculationRow
                    left={
                      <BreakdownTooltip
                        trigger="Other W-2 income (YTD)"
                        content={<OtherW2IncomeTooltip />}
                      />
                    }
                    center={
                      <PencilLinkOrText
                        isActiveEstimate={isActiveEstimate}
                        hideLink={isSample}
                      >
                        {centsToDollarsOrDashes(taxCalc?.wIncomeInCents)}
                      </PencilLinkOrText>
                    }
                    right={
                      <Text variant={TypographyStyles.H3}>
                        {centsToDollarsOrDashes(
                          taxCalc?.annualizedWIncomeInCents
                        )}
                      </Text>
                    }
                    inCard
                  />
                )}

                {isNumber(taxCalc?.estimatedAnnualW2IncomeInCents) && (
                  <CalculationRow
                    left={
                      <BreakdownTooltip
                        trigger="Other W-2 income (Estimated)"
                        content={<OtherW2IncomeEstimatedTooltip />}
                      />
                    }
                    center={
                      <PencilLinkOrText
                        isActiveEstimate={isActiveEstimate}
                        hideLink={isSample}
                      >
                        {centsToDollarsOrDashes(
                          taxCalc?.estimatedAnnualW2IncomeInCents
                        )}
                      </PencilLinkOrText>
                    }
                    right={
                      <Text variant={TypographyStyles.H3}>
                        {centsToDollarsOrDashes(
                          taxCalc?.annualizedWIncomeInCents
                        )}
                      </Text>
                    }
                    inCard
                  />
                )}

                {isNumber(taxCalc?.otherIndividualIncomeInCents) && (
                  <CalculationRow
                    left={
                      <BreakdownTooltip
                        trigger={
                          isScorp ? '1099 income' : 'Additional 1099 income'
                        }
                        content={
                          isScorp ? (
                            <Scorp1099IncomeTooltip />
                          ) : (
                            <Sprop1099IncomeTooltip />
                          )
                        }
                      />
                    }
                    center={
                      <PencilLinkOrText
                        isActiveEstimate={isActiveEstimate}
                        hideLink={isSample}
                      >
                        {centsToDollarsOrDashes(
                          taxCalc.otherIndividualIncomeInCents
                        )}
                      </PencilLinkOrText>
                    }
                    right={
                      <Text variant={TypographyStyles.H3}>
                        {centsToDollarsOrDashes(
                          taxCalc?.otherIndividualIncomeInCents
                        )}
                      </Text>
                    }
                    inCard
                  />
                )}

                {isNumber(taxCalc?.spouseWIncomeInCents) && (
                  <CalculationRow
                    left={<Text>Spouse&apos;s W-2 income (YTD)</Text>}
                    center={
                      <PencilLinkOrText
                        isActiveEstimate={isActiveEstimate}
                        hideLink={isSample}
                      >
                        {centsToDollarsOrDashes(taxCalc.spouseWIncomeInCents)}
                      </PencilLinkOrText>
                    }
                    right={
                      <Text variant={TypographyStyles.H3}>
                        {centsToDollarsOrDashes(
                          taxCalc?.annualizedSpouseWIncomeInCents
                        )}
                      </Text>
                    }
                    inCard
                  />
                )}

                {isNumber(taxCalc?.estimatedAnnualSpouseW2IncomeInCents) && (
                  <CalculationRow
                    left={<Text>Spouse&apos;s W-2 income (Estimated)</Text>}
                    center={
                      <PencilLinkOrText
                        isActiveEstimate={isActiveEstimate}
                        hideLink={isSample}
                      >
                        {centsToDollarsOrDashes(
                          taxCalc.estimatedAnnualSpouseW2IncomeInCents
                        )}
                      </PencilLinkOrText>
                    }
                    right={
                      <Text variant={TypographyStyles.H3}>
                        {centsToDollarsOrDashes(
                          taxCalc.annualizedSpouseWIncomeInCents
                        )}
                      </Text>
                    }
                    inCard
                  />
                )}

                {hasSpouse &&
                  isNumber(taxCalc?.spouseIndividualIncomeInCents) && (
                    <CalculationRow
                      left={<Text>Spouse&apos;s 1099 income</Text>}
                      center={
                        <PencilLinkOrText
                          isActiveEstimate={isActiveEstimate}
                          hideLink={isSample}
                        >
                          {centsToDollarsOrDashes(
                            taxCalc.spouseIndividualIncomeInCents
                          )}
                        </PencilLinkOrText>
                      }
                      right={
                        <Text variant={TypographyStyles.H3}>
                          {centsToDollarsOrDashes(
                            taxCalc.spouseIndividualIncomeInCents
                          )}
                        </Text>
                      }
                      inCard
                    />
                  )}
              </div>
            </Card>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion>

      <Accordion type="single" variant="text" collapsible>
        <Accordion.Item value="deductions">
          <Accordion.Trigger className="w-full">
            <AccordionHeader
              left={<Button variant="discreet">Deductions</Button>}
              right={
                <Text variant={TypographyStyles.H3}>
                  -
                  {centsToDollarsOrDashes(
                    centsToCurrency(
                      taxCalculationOutput?.data.federalStandardDeductionInCents
                    )
                      .add(
                        taxCalculationOutput?.data.annualQBIDeductionInCents ||
                          0
                      )
                      .add(
                        taxCalculationOutput?.data
                          .deductibleSelfEmploymentTaxInCents || 0
                      )
                  )}
                </Text>
              }
            />
          </Accordion.Trigger>
          <Accordion.Content>
            <Card color={CardColors.LEVEL_1} className="p-4">
              <div className="space-y-4">
                {/* Header row */}
                <CalculationRow
                  left={
                    <Text
                      variant={TypographyStyles.EYEBROW}
                      className="text-darkGray"
                    >
                      deduction source
                    </Text>
                  }
                  right={
                    <Text
                      variant={TypographyStyles.EYEBROW}
                      className="text-darkGray text-right"
                    >
                      amount
                    </Text>
                  }
                  inCard
                />

                {/* Standard deduction row */}
                <CalculationRow
                  left={
                    <>
                      <Popover>
                        <Popover.Trigger asChild>
                          <Text
                            variant={TypographyStyles.BODY_SM}
                            className="underline cursor-pointer"
                          >
                            Standard deduction
                          </Text>
                        </Popover.Trigger>
                        <Popover.Content className="max-w-sm">
                          <StandardDeductionTooltip
                            year={year}
                            standardDeductionInCents={
                              taxCalculationOutput?.data
                                .federalStandardDeductionInCents
                            }
                          />
                        </Popover.Content>
                      </Popover>
                      {/* Shown on md+ */}
                      <div className="hidden md:block mt-2">
                        <DeductionStatement />
                      </div>
                    </>
                  }
                  right={
                    <Text variant={TypographyStyles.H3}>
                      {formatDeduction(
                        taxCalculationOutput?.data
                          .federalStandardDeductionInCents
                      )}
                    </Text>
                  }
                  inCard
                />
                {/* Shown on sm only */}
                <div className="block md:hidden">
                  <DeductionStatement />
                </div>

                {/* QBI deduction row */}
                <CalculationRow
                  left={
                    <>
                      <Popover>
                        <Popover.Trigger asChild>
                          <Text
                            variant={TypographyStyles.BODY_SM}
                            className="underline cursor-pointer"
                          >
                            Qualified business income (QBI) deduction
                          </Text>
                        </Popover.Trigger>
                        <Popover.Content className="max-w-sm">
                          <QBIDeductionTooltip />
                        </Popover.Content>
                      </Popover>
                      {transparencyEnabled &&
                        taxCalculationOutput?.data.annualQBIDeductionInCents ===
                          0 && (
                          <Text color={TextColors.MUTED}>
                            <i>
                              This is $0.00 most often because household income
                              is above the limit for the deduction. An S-corp
                              with no qualifying business income also lands
                              here.
                            </i>
                          </Text>
                        )}
                    </>
                  }
                  right={
                    <Text variant={TypographyStyles.H3}>
                      {formatDeduction(
                        taxCalculationOutput?.data.annualQBIDeductionInCents
                      )}
                    </Text>
                  }
                  inCard
                />

                {/* Self-employment tax deduction row */}
                {(!isScorp || hasSpouse) && (
                  <CalculationRow
                    left={
                      <Popover>
                        <Popover.Trigger asChild>
                          <Text
                            variant={TypographyStyles.BODY_SM}
                            className="underline cursor-pointer"
                          >
                            Deductible portion of self-employment tax
                          </Text>
                        </Popover.Trigger>
                        <Popover.Content className="max-w-sm">
                          <DeductiblePotionSelfEmploymentTooltip />
                        </Popover.Content>
                      </Popover>
                    }
                    right={
                      <Text variant={TypographyStyles.H3}>
                        {formatDeduction(
                          taxCalculationOutput?.data
                            .deductibleSelfEmploymentTaxInCents
                        )}
                      </Text>
                    }
                    inCard
                  />
                )}
                {transparencyEnabled && (
                  <Text color={TextColors.MUTED}>
                    <i>
                      These deductions apply to your federal estimate. States
                      use their own standard deduction, and the QBI and
                      self-employment tax deductions don&apos;t reduce state
                      taxable income.
                    </i>
                  </Text>
                )}
              </div>
            </Card>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion>

      <Separator />

      <CalculationRow
        left={
          <BreakdownTooltip
            trigger={
              <Text variant={TypographyStyles.H3}>Total Taxable Income</Text>
            }
            content={<TotalTaxableIncomeTooltip />}
          />
        }
        right={
          <Text variant={TypographyStyles.H3} className="text-right">
            {centsToDollarsOrDashes(
              taxCalculationOutput?.data.annualTaxableIncomeInCents
            )}
          </Text>
        }
      />
    </>
  )
}
